import { Box, PageTitle, Pagination, PaginationResults, Tab, TabGroup, TabPanel, Card } from '@base-app';
import { DEFAULT_PAGE_SIZE, INITIAL_PAGE } from '@components/constants';
import { TabContextProvider } from '@common/components';
import { PATHS } from '@constants/routes';
import { useAuthStore } from '@modules/auth/store';
import { isUserAdministrator } from '@modules/auth/utils/userRole';
import { TEAMS_TRANSLATION } from '@modules/teams/constants';
import { useQueryTeams } from '@modules/teams/hooks';
import { TeamListItem } from '@modules/teams/types';
import { teamListCompare } from '@modules/teams/utils';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AddTeamMemberButtonStyled,
  PaginationContainerStyled,
  TeamsListContainerStyled,
  TeamsListHeaderStyled,
  TeamsListSectionTitleStyled,
} from './TeamsListScreen.styles';

import TeamsList from '@modules/teams/components/TeamsList/TeamsList';
import { isSSOUser } from '@modules/core/utils';
import useOrganizationName from '@/modules/my-account/hooks/useOrganizationName';

const TeamsListScreen: FC = () => {
  const organizationName = useOrganizationName();
  const { t } = useTranslation(TEAMS_TRANSLATION);
  const { data: teamsData } = useQueryTeams();
  const { user: currentUser } = useAuthStore();
  const [data, setData] = useState<TeamListItem[]>([]);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<number>(INITIAL_PAGE);
  const [initialPageItem, setInitialPageItem] = useState<number>(INITIAL_PAGE);
  const [lastPageItem, setlastPageItem] = useState<number>(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    if (teamsData === undefined) {
      return;
    }

    teamsData.sort(teamListCompare);
    setData([...teamsData]);
  }, [teamsData]);

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * DEFAULT_PAGE_SIZE;
    const lastPageIndex = firstPageIndex + DEFAULT_PAGE_SIZE;
    setInitialPageItem(firstPageIndex + 1);
    data !== undefined && data.length < lastPageIndex ? setlastPageItem(data?.length) : setlastPageItem(lastPageIndex);
    return data?.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, data]);

  const handleAddTeamMemberClick = (): void => {
    navigate(PATHS.TEAMS_ADD_TEAM_MEMBER);
  };

  return (
    <TeamsListContainerStyled>
      <TeamsListHeaderStyled>
        <PageTitle>{t('title')}</PageTitle>
        {currentUser !== null &&
          isUserAdministrator(currentUser) &&
          organizationName &&
          !isSSOUser(currentUser, organizationName) && (
            <AddTeamMemberButtonStyled id="add-team-member-button" onClick={handleAddTeamMemberClick}>
              {t('add_team_member')}
            </AddTeamMemberButtonStyled>
          )}
      </TeamsListHeaderStyled>
      <Card
        css={{
          px: '$0',
        }}
      >
        <Box>
          <TeamsListSectionTitleStyled>{t('team_list')}</TeamsListSectionTitleStyled>
          <TabContextProvider value="members">
            <TabGroup
              value="members"
              css={{
                marginLeft: '$4',
                '@sm': {
                  marginLeft: '$8',
                },
              }}
            >
              <Tab label={t('members')} value="members" />
              <Tab label={t('activity_log')} value="log" disabled />
            </TabGroup>
            <TabPanel value="members">
              {currentUser !== null && <TeamsList currentUser={currentUser} data={currentTableData} />}
            </TabPanel>
          </TabContextProvider>
        </Box>
      </Card>
      {currentUser !== null && data !== undefined && data.length > DEFAULT_PAGE_SIZE && (
        <PaginationContainerStyled>
          <PaginationResults range={[initialPageItem, lastPageItem]} totalCount={data.length} />
          <Pagination
            currentPage={currentPage}
            totalCount={data.length}
            pageSize={DEFAULT_PAGE_SIZE}
            onPageChange={(page: number) => setCurrentPage(page)}
          />
        </PaginationContainerStyled>
      )}
    </TeamsListContainerStyled>
  );
};

export default TeamsListScreen;
